import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowRight, FiCamera, FiFileText } from 'react-icons/fi';
import usePlanUsage from '../hooks/usePlanUsage';
import {
  formatPlanResetDate,
  planUsageBarColor,
  planUsageSurfaceClass,
} from '../utils/planUsageDisplay';

function UsageMeter({
  label,
  used,
  limit,
  percent,
  tone,
  modoOscuro,
  icon: Icon,
  showPercentLine = true,
  barClassName = 'h-1.5',
}) {
  const barWidth = Math.min(Math.max(percent ?? 0, 0), 100);
  const barColor = planUsageBarColor(tone, modoOscuro);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className={`w-3.5 h-3.5 shrink-0 ${textMuted}`} />}
          <span className={`text-xs font-semibold truncate ${textPri}`}>{label}</span>
        </div>
        <span className={`text-xs font-semibold tabular-nums shrink-0 ${textPri}`}>
          {used} / {limit}
        </span>
      </div>
      <div className={`${barClassName} rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${barWidth}%`, backgroundColor: barColor }}
        />
      </div>
      {showPercentLine && (
        <p className={`text-[11px] mt-1 ${textMuted}`}>{percent}% usado</p>
      )}
    </div>
  );
}

export default function PlanUsageBanner({
  documentUsage = null,
  showOcr = false,
  modoOscuro = false,
  className = '',
  compact = false,
  embedded = false,
  /** default | compact | inline — inline = una línea, para formularios foco */
  variant = compact ? 'compact' : 'default',
}) {
  const resolvedVariant = variant || (compact ? 'compact' : 'default');
  const isInline = resolvedVariant === 'inline';
  const isCompact = resolvedVariant === 'compact' || isInline;
  const navigate = useNavigate();
  const { documents, ocr, loading } = usePlanUsage({
    enabled: true,
    documentUsage,
    fetchRemote: showOcr || !documentUsage,
  });

  if (loading && !documents) return null;
  if (!documents || documents.unlimited) return null;

  const tone = documents.status.tone;
  const surface = planUsageSurfaceClass(tone, modoOscuro);
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const resetLabel = formatPlanResetDate(documents.resetDate);
  const showOcrMeter = showOcr && ocr && !ocr.unlimited && ocr.limit > 0;
  const ctaLabel = tone === 'full' ? 'Ampliar uso' : 'Ver detalle';
  const ctaHref = tone === 'full'
    ? '/uso-plan#packs-section'
    : showOcrMeter && ocr.tone === 'full'
      ? '/uso-plan#ocr-packs'
      : '/uso-plan';

  const ctaButtonClass = tone === 'full'
    ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
    : modoOscuro
      ? 'border-gray-700 text-gray-200 hover:bg-gray-800'
      : 'border-gray-200 text-gray-700 hover:bg-gray-50';

  const ctaLinkClass = tone === 'full'
    ? modoOscuro ? 'text-red-300 hover:text-red-200' : 'text-red-600 hover:text-red-700'
    : modoOscuro ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900';

  if (isInline) {
    const showStatusLine = tone !== 'ok' || documents.extraDocuments > 0;
    const barWidth = Math.min(Math.max(documents.percent ?? 0, 0), 100);
    const barColor = planUsageBarColor(tone, modoOscuro);
    const shellClass = embedded
      ? `w-full ${className}`.trim()
      : `rounded-lg border px-3 py-2 ${surface} ${className}`.trim();

    return (
      <section
        className={shellClass}
        aria-label="Uso del plan este mes"
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className={`flex items-center gap-1 text-[11px] font-medium truncate ${textSec}`}>
                <FiFileText className="w-3 h-3 shrink-0 opacity-70" />
                Registros del mes
              </span>
              <span className={`text-[11px] font-semibold tabular-nums shrink-0 ${textPri}`}>
                {documents.used}/{documents.effectiveLimit}
              </span>
            </div>
            <div className={`h-1 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${barWidth}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(ctaHref)}
            className={`inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold whitespace-nowrap ${ctaLinkClass}`}
          >
            {ctaLabel}
            <FiArrowRight className="w-3 h-3" />
          </button>
        </div>
        {showStatusLine && (
          <p className={`mt-1.5 text-[11px] leading-snug ${textSec}`}>
            {tone === 'full' && (
              <FiAlertTriangle className={`inline w-3 h-3 mr-1 -mt-0.5 ${modoOscuro ? 'text-red-300' : 'text-red-600'}`} />
            )}
            {documents.status.label}
            {documents.extraDocuments > 0 ? ` · +${documents.extraDocuments} extra` : ''}
            {tone !== 'full' && documents.remaining != null
              ? ` · Quedan ${documents.remaining}`
              : ''}
            {resetLabel ? ` · Renueva ${resetLabel}` : ''}
          </p>
        )}
      </section>
    );
  }

  return (
    <section className={`rounded-2xl border p-3 sm:p-4 ${surface} ${className}`}>
      <div className={`flex flex-col gap-3 ${isCompact ? '' : 'sm:flex-row sm:items-center sm:justify-between'}`}>
        <div className={`min-w-0 flex-1 ${isCompact ? 'space-y-2' : 'space-y-3'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {tone === 'full' && (
                  <FiAlertTriangle className={`w-4 h-4 shrink-0 ${modoOscuro ? 'text-red-300' : 'text-red-600'}`} />
                )}
                <h2 className={`text-sm font-semibold ${textPri}`}>Uso del plan este mes</h2>
              </div>
              <p className={`text-xs mt-0.5 ${textSec}`}>
                {documents.status.label}
                {documents.extraDocuments > 0 ? ` · +${documents.extraDocuments} extra` : ''}
                {resetLabel ? ` · Se renueva el ${resetLabel}` : ''}
              </p>
            </div>
            {!isInline && (
              <button
                type="button"
                onClick={() => navigate(ctaHref)}
                className={`hidden sm:inline-flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${ctaButtonClass}`}
              >
                {ctaLabel}
                <FiArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <UsageMeter
            label="Registros de venta"
            used={documents.used}
            limit={documents.effectiveLimit}
            percent={documents.percent}
            tone={tone}
            modoOscuro={modoOscuro}
            icon={FiFileText}
            showPercentLine={!isCompact}
          />

          {showOcrMeter && (
            <UsageMeter
              label="Escaneos OCR"
              used={ocr.used}
              limit={ocr.limit}
              percent={ocr.percent}
              tone={ocr.tone}
              modoOscuro={modoOscuro}
              icon={FiCamera}
              showPercentLine={!isCompact}
            />
          )}

          {!isCompact && documents.remaining != null && tone !== 'full' && (
            <p className={`text-[11px] ${textSec}`}>
              Te quedan <span className="font-semibold">{documents.remaining}</span> registro{documents.remaining === 1 ? '' : 's'} este mes.
            </p>
          )}
        </div>

        {!isCompact && (
          <button
            type="button"
            onClick={() => navigate(ctaHref)}
            className={`inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold sm:w-auto sm:hidden ${ctaButtonClass}`}
          >
            {ctaLabel}
            <FiArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        {isCompact && !isInline && (
          <button
            type="button"
            onClick={() => navigate(ctaHref)}
            className={`sm:hidden inline-flex w-full items-center justify-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold ${ctaButtonClass}`}
          >
            {ctaLabel}
            <FiArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </section>
  );
}
